// Dependencies
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";

// Material UI Components
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";

// Helpers
import { formatAddress } from "helpers/string-format-helpers";
import { baseReducer } from "reducers/base-reducer";

const LocationAutocomplete = ({
  handleLocation,
  types,
  onClear,
  textFieldProps,
  initialValue,
  shareState,
  popupIcon,
  disableDropdownIcon,
  ...rest
}) => {
  const { placesService, placePredictions, getPlacePredictions } =
    usePlacesService({
      options: {
        componentRestrictions: { country: "ca" },
        types,
      },
      debounce: 200,
    });

  const dispatch = useDispatch();
  const locationAutocompleteValue = useSelector(
    (s) => s.global.locationAutocompleteValue,
  );

  const initialState = () => {
    if (locationAutocompleteValue) return locationAutocompleteValue;
    if (initialValue?.formatted_address) {
      return { ...initialValue, description: initialValue.formatted_address };
    }
    if (initialValue?.city) {
      return { ...initialValue, description: formatAddress(initialValue) };
    }
    return "";
  };

  const [value, setValue] = useState(() => initialState());

  // this is needed for inv page - if shareState = true, use reducer
  const locationValue = shareState ? locationAutocompleteValue : value;

  // generic setter for inv page
  const setLocation = (locationVal) => {
    if (shareState) {
      dispatch(
        baseReducer.actions.updateLocationAutocompleteValue(locationVal),
      );
    } else {
      setValue(locationVal);
    }
  };

  useEffect(() => {
    if (placesService && initialValue?.googlePlaceId) {
      placesService.getDetails(
        { placeId: initialValue?.googlePlaceId },
        (placeDetails) => {
          if (placeDetails?.formatted_address) {
            setLocation({
              ...placeDetails,
              description: placeDetails.formatted_address,
            });
          }
        },
      );
    }
  }, [placesService]);

  const onInputChange = (evt, _, reason) => {
    if (reason === "clear") {
      setLocation(null);
      onClear();
    } else {
      getPlacePredictions({ input: evt?.target.value });
    }
  };

  const onChange = (_, selected) => {
    placesService.getDetails(
      { placeId: selected?.place_id },
      (placeDetails) => {
        setLocation({
          ...placeDetails,
          description: placeDetails.formatted_address,
        });
        handleLocation(placeDetails);
      },
    );
  };

  useEffect(() => {
    // if shareState, we want to update initial state
    if (shareState) {
      setLocation(initialState());
    }
  }, []);

  return (
    <Autocomplete
      options={placePredictions}
      renderInput={(params) => <TextField {...params} {...textFieldProps} />}
      value={locationValue}
      getOptionLabel={(option) => option.description || ""}
      isOptionEqualToValue={(option, val) => option.place_id === val.place_id}
      onInputChange={onInputChange}
      onChange={onChange}
      popupIcon={popupIcon || (disableDropdownIcon && <div />)}
      data-cy="location-autocomplete"
      {...rest}
    />
  );
};

LocationAutocomplete.propTypes = {
  handleLocation: PropTypes.func.isRequired,
  types: PropTypes.arrayOf(PropTypes.string),
  onClear: PropTypes.func,
  textFieldProps: PropTypes.shape(),
  shareState: PropTypes.bool,
};

LocationAutocomplete.defaultProps = {
  types: [],
  onClear: () => {},
  textFieldProps: {},
  shareState: false,
};

export default LocationAutocomplete;
